/**
 * @file Shared token-refresh state consumed by both the Axios request
 * interceptor (`axios-interceptor.ts`) and the browser-event refresher
 * (`events.ts`).
 *
 * Coordinates refresh across:
 * - **Same tab**: A single module-scoped `refreshPromise` prevents duplicate
 *   calls regardless of whether the interceptor or event listener triggers
 *   first.
 * - **Cross tabs**: A localStorage lock with a TTL ensures only one tab calls
 *   the refresh endpoint at a time. Other tabs poll until the cookie updates.
 *
 * The non-httpOnly `refresh_at` cookie (set by the server on every token write)
 * is the single source of truth for "do we need to refresh?". Because cookies
 * are shared across tabs by the browser, once *any* tab completes a refresh the
 * cookie updates for *all* tabs simultaneously.
 */

import axios from 'axios';
import { isNullish } from '@/utils/null-utils';
import { Time } from '@/utils/time-utils';
import {
  withBackoffRetry,
  BackoffStrategy
} from '@/utils/network-utils/RetryWithBackoff';
import { TOKEN_REFRESH_CONFIG } from './config';
import { isBrowser, acquireLock, releaseLock, setReloadFlag } from './utils';

const { cookieRegex: REFRESH_AT_REGEX, lockPollMs: LOCK_POLL_MS } =
  TOKEN_REFRESH_CONFIG;

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

/**
 * In-flight refresh promise shared between the interceptor and the event
 * listener within a single tab. Both writers set this; both readers await it.
 */
let refreshPromise: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Cookie check
// ---------------------------------------------------------------------------

/**
 * Reads the non-httpOnly `refresh_at` cookie to determine whether the current
 * tokens are stale.
 *
 * Returns `true` (needs refresh) when:
 * - The cookie is missing (no tokens at all, e.g. CDN cache hit).
 * - The current time has passed the pre-computed `refresh_at` timestamp.
 *
 * Returns `false` when:
 * - Not running in a browser.
 * - Tokens are still fresh.
 *
 * @returns Whether a token refresh is needed.
 */
export function needsRefresh(): boolean {
  if (!isBrowser()) return false;

  const match = REFRESH_AT_REGEX.exec(document.cookie);
  if (isNullish(match)) return true;

  const refreshAt = Number(match[1]);
  if (Number.isNaN(refreshAt)) return true;

  return Time.nowInSeconds() > refreshAt;
}

// ---------------------------------------------------------------------------
// Promise accessors
// ---------------------------------------------------------------------------

/**
 * Returns the in-flight refresh promise, or `null` if idle.
 * @returns The in-flight refresh promise, or `null`.
 */
export function getRefreshPromise(): Promise<void> | null {
  return refreshPromise;
}

// ---------------------------------------------------------------------------
// Refresh orchestration
// ---------------------------------------------------------------------------

/**
 * Ensures a single token-refresh call is in flight. Concurrent callers within
 * the same tab share the same promise. Across tabs, a localStorage lock
 * prevents duplicate SLAS calls.
 *
 * When another tab holds the lock this tab polls until either:
 * 1. The cookie is updated (refresh succeeded elsewhere) — resolves immediately.
 * 2. The lock expires (other tab crashed) — this tab takes over.
 *
 * @param refreshUrl - The server endpoint to POST to (e.g. `/api/auth/refresh-tokens`).
 * @returns A promise that resolves once tokens are fresh.
 */
export async function ensureRefresh(refreshUrl: string): Promise<void> {
  // Same-tab dedup: return the existing in-flight promise.
  if (refreshPromise) return refreshPromise;

  // Cross-tab: another tab holds the lock — poll until cookie updates.
  if (!acquireLock()) {
    refreshPromise = new Promise<void>((resolve) => {
      const poll = (): void => {
        // Cookie was updated by the other tab — done.
        if (!needsRefresh()) {
          refreshPromise = null;
          resolve();
          return;
        }

        // Lock expired or still stale — attempt to take over.
        if (acquireLock()) {
          // eslint-disable-next-line no-void -- Fire-and-forget within the
          // enclosing Promise constructor; `resolve` handles settlement.
          void doRefresh(refreshUrl).then(resolve);
          return;
        }

        setTimeout(poll, LOCK_POLL_MS);
      };

      setTimeout(poll, LOCK_POLL_MS);
    });

    return refreshPromise;
  }

  // This tab owns the lock — perform the actual refresh.
  refreshPromise = doRefresh(refreshUrl);
  return refreshPromise;
}

/**
 * Calls the server refresh endpoint. On unrecoverable failure triggers a
 * guarded page reload as a last resort.
 * @param refreshUrl - The server endpoint to POST to.
 */
async function doRefresh(refreshUrl: string): Promise<void> {
  try {
    // Retry once with a 1 s delay to absorb transient network errors before
    // falling back to a full page reload.
    await withBackoffRetry(async () => axios.post(refreshUrl), {
      retries: 1,
      initialDelay: 1000,
      strategy: BackoffStrategy.Constant
    });
  } catch (error) {
    // All retries exhausted — reload so the server middleware can issue
    // fresh tokens on the document request.
    console.error(
      '[token-refresh] doRefresh failed after retry, reloading page:',
      error
    );
    if (isBrowser()) {
      setReloadFlag();
      window.location.reload();
    }
  } finally {
    releaseLock();
    refreshPromise = null;
  }
}
